import { useState } from 'react';
import PropTypes from 'prop-types';
import ContactInlineSelector from '../Home/rechargeElements/ContactInlineSelector';
import { cleanNum, regex } from '../../utils/common';

/**
 * Wrapper reutilizable sobre ContactInlineSelector.
 * Gestiona internamente el estado del input y la lógica de agregar/quitar contactos.
 *
 * Props:
 *   form            – instancia de react-hook-form
 *   type            – 'movil' | 'nauta'  (ContactInlineSelector sólo renderiza movil)
 *   options         – array de opciones de contactos { name, value, phone?, email? }
 *   isLoading       – boolean
 *   expandAll       – boolean
 *   maxVisible      – number
 *   placeholder     – string
 *   icon            – ReactElement
 *   onContactsChange– callback opcional (nextContacts) => void  (ej: actualizar caché)
 */
const ContactInlineSelectorField = ({
  form,
  type = 'movil',
  options = [],
  isLoading = false,
  expandAll,
  maxVisible,
  placeholder = 'Ej: +53 5XXX XXXX',
  icon,
  onContactsChange,
}) => {
  const [inputValue, setInputValue] = useState('');

  const currentContacts = form.watch('contacts') || [];

  const updateContacts = (nextContacts) => {
    form.setValue('contacts', nextContacts, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    form.clearErrors('contacts');
    form.trigger('contacts');
    onContactsChange?.(nextContacts);
  };

  const handleAddContact = (option) => {
    const rawInput =
      typeof option === 'string' ? option : option?.value || inputValue;

    if (!String(rawInput || '').trim()) {
      form.setError('contacts', {
        type: 'manual',
        message:
          type === 'movil'
            ? 'Debes agregar al menos un teléfono válido'
            : 'Debes agregar al menos una cuenta Nauta válida',
      });
      return;
    }

    if (type === 'movil') {
      const phone = cleanNum(rawInput).replace(/^\+53/, '');

      if (!/^[56]\d{7}$/.test(phone)) {
        form.setError('contacts', {
          type: 'manual',
          message: 'Teléfono inválido, debe tener 8 dígitos',
        });
        return;
      }

      if (currentContacts.some((c) => c.phone === phone)) {
        form.setError('contacts', {
          type: 'manual',
          message: 'Ese número ya fue agregado',
        });
        return;
      }

      const contactInOptions = option && options.find(o => o.value === phone);

      const newContact = { phone, name: contactInOptions?.name, value: phone };

      updateContacts([
        ...currentContacts,
        newContact,
      ]);
      setInputValue('');
      return;
    }

    // nauta
    const email = String(rawInput).trim().toLowerCase();

    if (!regex.nauta.test(email)) {
      form.setError('contacts', {
        type: 'manual',
        message: 'Cuenta Nauta inválida',
      });
      return;
    }

    if (currentContacts.some((c) => c.email === email)) {
      form.setError('contacts', {
        type: 'manual',
        message: 'Esa cuenta ya fue agregada',
      });
      return;
    }

    updateContacts([
      ...currentContacts,
      { email, name: option?.name, value: email },
    ]);
    setInputValue('');
  };

  const handleRemoveContact = (value) => {
    const nextContacts = currentContacts.filter((c) =>
      type === 'movil' ? c.phone !== value : c.email !== value,
    );
    updateContacts(nextContacts);
  };

  const getError = () => {
    const error = form.formState.errors.contacts;
    if (!error) return '';
    if (Array.isArray(error))
      return error[0]?.phone?.message || error[0]?.email?.message || '';
    return error.message || '';
  };

  return (
    <ContactInlineSelector
      type={type}
      contacts={currentContacts}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(cleanNum(value));
        form.clearErrors('contacts');
      }}
      onAddContact={handleAddContact}
      onRemoveContact={handleRemoveContact}
      options={options}
      placeholder={placeholder}
      error={getError()}
      icon={icon}
      isLoading={isLoading}
      expandAll={expandAll}
      maxVisible={maxVisible}
    />
  );
};

ContactInlineSelectorField.propTypes = {
  form: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['movil', 'nauta']),
  options: PropTypes.array,
  isLoading: PropTypes.bool,
  expandAll: PropTypes.bool,
  maxVisible: PropTypes.number,
  placeholder: PropTypes.string,
  icon: PropTypes.element,
  onContactsChange: PropTypes.func,
};

export default ContactInlineSelectorField;
